import type {
  ContactCompany,
  ContactCompanyRole,
} from '@otw/models/leads/contactCompanies'

export interface ContactCompanyMembership {
  company: ContactCompany
  role: ContactCompanyRole
}

/** Companies this contact belongs to, derived from `contact_companies.members`. */
export function getContactMemberships(
  contactId: string,
  companies: ContactCompany[],
): ContactCompanyMembership[] {
  const trimmedId = contactId.trim()
  if (!trimmedId) return []

  return companies
    .flatMap((company) => {
      const member = company.members.find((m) => m.contactId === trimmedId)
      if (!member) return []
      return [{ company, role: member.role }]
    })
    .sort((a, b) => a.company.name.localeCompare(b.company.name))
}

/**
 * One-pass index of contactId → company names (sorted).
 * Prefer this over calling {@link getContactCompanyNames} per contact for large lists.
 */
export function buildContactCompanyNamesIndex(
  companies: ContactCompany[],
): Map<string, string[]> {
  const index = new Map<string, string[]>()

  for (const company of companies) {
    const name = company.name?.trim() || 'Unnamed company'
    for (const member of company.members ?? []) {
      const contactId = member.contactId?.trim()
      if (!contactId) continue
      const existing = index.get(contactId)
      if (existing) {
        if (!existing.includes(name)) existing.push(name)
      } else {
        index.set(contactId, [name])
      }
    }
  }

  for (const names of index.values()) {
    names.sort((a, b) => a.localeCompare(b))
  }

  return index
}

export function getContactMemberCompanyIds(
  contactId: string,
  companies: ContactCompany[],
): string[] {
  return getContactMemberships(contactId, companies).map((m) => m.company.id)
}

export function getContactCompanyNames(
  contactId: string,
  companies: ContactCompany[],
): string[] {
  return getContactMemberships(contactId, companies).map((m) => m.company.name)
}

/** Prefer owner role, otherwise first membership by name. */
export function getContactPrimaryCompany(
  contactId: string,
  companies: ContactCompany[],
): ContactCompany | undefined {
  const memberships = getContactMemberships(contactId, companies)
  const ownerMembership = memberships.find((m) => m.role === 'owner')
  return (ownerMembership ?? memberships[0])?.company
}

/** Prefer owner role, otherwise first membership by name. */
export function getContactPrimaryCompanyName(
  contactId: string,
  companies: ContactCompany[],
): string | undefined {
  return getContactPrimaryCompany(contactId, companies)?.name
}

export function getContactCompanySearchText(
  contactId: string,
  companies: ContactCompany[],
): string {
  return getContactCompanyNames(contactId, companies).join(' ')
}

export function diffContactCompanyMemberships(
  contactId: string,
  companies: ContactCompany[],
  selectedCompanyIds: string[],
): { toAdd: string[]; toRemove: string[] } {
  const currentIds = new Set(getContactMemberCompanyIds(contactId, companies))
  const selectedIds = new Set(selectedCompanyIds.map((id) => id.trim()).filter(Boolean))
  const toAdd = [...selectedIds].filter((id) => !currentIds.has(id))
  const toRemove = [...currentIds].filter((id) => !selectedIds.has(id))
  return { toAdd, toRemove }
}

export interface ContactCompanyMembershipSelection {
  companyId: string
  role: ContactCompanyRole
}

export function diffContactCompanyMembershipSelections(
  contactId: string,
  companies: ContactCompany[],
  selections: ContactCompanyMembershipSelection[],
): {
  toAdd: ContactCompanyMembershipSelection[]
  toRemove: string[]
  toUpdateRole: ContactCompanyMembershipSelection[]
} {
  const currentMemberships = getContactMemberships(contactId, companies)
  const currentByCompanyId = new Map(
    currentMemberships.map((membership) => [membership.company.id, membership.role]),
  )
  const selectedByCompanyId = new Map(
    selections.map((selection) => [selection.companyId.trim(), selection.role]),
  )

  const toAdd: ContactCompanyMembershipSelection[] = []
  const toUpdateRole: ContactCompanyMembershipSelection[] = []

  for (const [companyId, role] of selectedByCompanyId) {
    if (!companyId) continue
    const currentRole = currentByCompanyId.get(companyId)
    if (!currentRole) {
      toAdd.push({ companyId, role })
      continue
    }
    if (currentRole !== role) {
      toUpdateRole.push({ companyId, role })
    }
  }

  const toRemove = [...currentByCompanyId.keys()].filter(
    (companyId) => !selectedByCompanyId.has(companyId),
  )

  return { toAdd, toRemove, toUpdateRole }
}
